import TagManager from 'react-gtm-module';
import type { CookieConsentPreferences } from '../contexts/CookieConsentContext';
import { API_CONFIG } from '../config/api';

// GTM Configuration
const GTM_ID = 'GTM-TXBSGS5F';
const GA_ID = 'G-7CKMYS7N33';
const API_BASE_URL = API_CONFIG.BASE_URL;

// Track initialization state
let gtmInitialized = false;
let serverSideGAEnabled = false;

// Generate or retrieve client ID for GA4
const getClientId = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const storageKey = 'ga_client_id';
  let clientId = localStorage.getItem(storageKey);

  if (!clientId) {
    // Generate a UUID v4-like client ID
    clientId = `${Date.now()}.${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(storageKey, clientId);
  }

  return clientId;
};

/**
 * Initialize Google Tag Manager
 * Should be called after consent is given
 */
export const initializeGTM = (preferences: CookieConsentPreferences | null): void => {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    // Only initialize if analytics consent is granted
    if (preferences && preferences.analytics_Storage === 'granted') {
      if (gtmInitialized) {
        console.log('[GTM] Already initialized');
        return;
      }

      TagManager.initialize({
        gtmId: GTM_ID,
      });

      gtmInitialized = true;
      console.log('[GTM] Initialized successfully');
    } else {
      console.log('[GTM] Not initialized - analytics consent not granted');
    }
  } catch (error) {
    console.error('[GTM] Failed to initialize:', error);
  }
};

/**
 * Initialize Google Analytics (Server-Side)
 * Should be called after consent is given
 */
export const initializeGA = (preferences: CookieConsentPreferences | null): void => {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    // Only initialize if analytics consent is granted
    if (preferences && preferences.analytics_Storage === 'granted') {
      if (serverSideGAEnabled) {
        console.log('[GA] Already initialized (server-side)');
        return;
      }

      // Initialize client ID
      getClientId();
      
      serverSideGAEnabled = true;
      console.log('[GA] Server-side tracking enabled');
    } else {
      console.log('[GA] Not initialized - analytics consent not granted');
    }
  } catch (error) {
    console.error('[GA] Failed to initialize:', error);
  }
};

/**
 * Initialize both GTM and GA
 */
export const initializeAnalytics = (preferences: CookieConsentPreferences | null): void => {
  initializeGTM(preferences);
  initializeGA(preferences);
};

/**
 * Track an event in GTM dataLayer
 */
export const trackGTMEvent = (eventName: string, eventData?: Record<string, any>): void => {
  try {
    if (!gtmInitialized) {
      console.warn('[GTM] Cannot track event - GTM not initialized');
      return;
    }

    TagManager.dataLayer({
      event: eventName,
      ...eventData,
    });

    console.log('[GTM] Event tracked:', eventName, eventData);
  } catch (error) {
    console.error('[GTM] Failed to track event:', error);
  }
};

/**
 * Track an event in Google Analytics (Server-Side)
 */
export const trackGAEvent = async (
  action: string,
  category?: string,
  label?: string,
  value?: number,
  userId?: string | null,
  userProperties?: Record<string, any>
): Promise<void> => {
  try {
    if (!serverSideGAEnabled) {
      console.warn('[GA] Cannot track event - GA not initialized');
      return;
    }

    const clientId = getClientId();
    if (!clientId) {
      console.warn('[GA] Cannot track event - no client ID');
      return;
    }

    const eventParams: Record<string, any> = {};
    if (category) eventParams.event_category = category;
    if (label) eventParams.event_label = label;
    if (value !== undefined) eventParams.value = value;

    const response = await fetch(`${API_BASE_URL}/api/analytics/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        clientId,
        eventName: action,
        eventParams,
        userProperties: userProperties || {},
        userId: userId || null,
      }),
    });

    if (!response.ok) {
      console.error('[GA] Failed to send event to server:', response.status);
      return;
    }

    console.log('[GA] Event tracked (server-side):', { action, category, label, value });
  } catch (error) {
    console.error('[GA] Failed to track event:', error);
  }
};

/**
 * Track an event in both GTM and GA (Server-Side)
 */
export const trackEvent = async (
  eventName: string,
  eventData?: Record<string, any>,
  gaConfig?: {
    category?: string;
    label?: string;
    value?: number;
  }
): Promise<void> => {
  // Track in GTM (client-side)
  trackGTMEvent(eventName, eventData);
  
  // Track in GA (server-side)
  if (gaConfig) {
    await trackGAEvent(
      eventName,
      gaConfig.category,
      gaConfig.label,
      gaConfig.value,
      getUserId(),
      getUserProperties()
    );
  } else {
    // Default GA tracking with event name as action
    await trackGAEvent(
      eventName,
      eventData?.category,
      eventData?.label,
      eventData?.value,
      getUserId(),
      getUserProperties()
    );
  }
};

/**
 * Track page view in GA (Server-Side)
 */
export const trackPageView = async (
  path: string,
  title?: string,
  userId?: string | null,
  userProperties?: Record<string, any>
): Promise<void> => {
  try {
    if (!serverSideGAEnabled) {
      console.warn('[GA] Cannot track page view - GA not initialized');
      return;
    }

    const clientId = getClientId();
    if (!clientId) {
      console.warn('[GA] Cannot track page view - no client ID');
      return;
    }

    const response = await fetch(`${API_BASE_URL}/api/analytics/pageview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        clientId,
        pagePath: path,
        pageTitle: title || document.title,
        pageLocation: window.location.href,
        userProperties: userProperties || {},
        userId: userId || null,
      }),
    });

    if (!response.ok) {
      console.error('[GA] Failed to send page view to server:', response.status);
      return;
    }

    console.log('[GA] Page view tracked (server-side):', path);
  } catch (error) {
    console.error('[GA] Failed to track page view:', error);
  }
};

/**
 * Set user properties in GA (Server-Side)
 * Note: User properties are now sent with each event
 */
export const setGAUserProperties = (properties: Record<string, any>): void => {
  // Store user properties for use in subsequent events
  if (typeof window !== 'undefined') {
    (window as any).__gaUserProperties = properties;
    console.log('[GA] User properties stored:', properties);
  }
};

/**
 * Set user ID in GA (Server-Side)
 * Note: User ID is now sent with each event
 */
export const setGAUserId = (userId: string): void => {
  // Store user ID for use in subsequent events
  if (typeof window !== 'undefined') {
    (window as any).__gaUserId = userId;
    console.log('[GA] User ID stored:', userId);
  }
};

/**
 * Get stored user properties
 */
const getUserProperties = (): Record<string, any> => {
  if (typeof window !== 'undefined' && (window as any).__gaUserProperties) {
    return (window as any).__gaUserProperties;
  }
  return {};
};

/**
 * Get stored user ID
 */
const getUserId = (): string | null => {
  if (typeof window !== 'undefined' && (window as any).__gaUserId) {
    return (window as any).__gaUserId;
  }
  return null;
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
    __gaUserProperties?: Record<string, any>;
    __gaUserId?: string;
  }
}

